import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

import type {
  AddressType,
  CreateSavedAddressReq,
  SavedAddress,
  UpdateSavedAddressReq,
} from '@/types/parcel/userData';

interface UseSavedAddressesProps {
  initialType?: AddressType;
  autoFetch?: boolean;
}

export const useSavedAddresses = (props?: UseSavedAddressesProps) => {
  const initialType = props?.initialType;
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAddresses = useCallback(async (type?: AddressType) => {
    setLoading(true);
    setError(null);
    try {
      const http = initializeHttpProvider();
      const response = await http.userData.getSavedAddresses(type);
      if (response && response.success && Array.isArray(response.data)) {
        setAddresses(response.data);
      } else {
        setAddresses([]);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al obtener direcciones guardadas';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAddress = useCallback(async (data: CreateSavedAddressReq) => {
    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.userData.createSavedAddress(data);
      if (response && response.success && response.data) {
        setAddresses((prev) => [response.data, ...prev]);
        toast.success('Dirección guardada exitosamente');
        return response.data;
      }
      throw new Error(response?.message || 'Error al guardar la dirección');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'No se pudo guardar la dirección';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAddress = useCallback(async (addressId: string, data: UpdateSavedAddressReq) => {
    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.userData.updateSavedAddress(addressId, data);
      if (response && response.success && response.data) {
        setAddresses((prev) => prev.map((addr) => (addr.id === addressId ? response.data : addr)));
        toast.success('Dirección actualizada exitosamente');
        return response.data;
      }
      throw new Error(response?.message || 'Error al actualizar la dirección');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'No se pudo actualizar la dirección';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pingAddress = useCallback(async (addressId: string) => {
    try {
      const http = initializeHttpProvider();
      const response = await http.userData.pingSavedAddress(addressId);
      if (response && response.success && response.data) {
        setAddresses((prev) => prev.map((addr) => (addr.id === addressId ? response.data : addr)));
      }
    } catch {
      // Ping en segundo plano; no bloquea al usuario ni emite error ruidoso
    }
  }, []);

  const deleteAddress = useCallback(async (addressId: string) => {
    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.userData.deleteSavedAddress(addressId);
      if (response && response.success) {
        setAddresses((prev) => prev.filter((addr) => addr.id !== addressId));
        toast.success('Dirección eliminada correctamente');
        return true;
      }
      throw new Error(response?.message || 'Error al eliminar la dirección');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'No se pudo eliminar la dirección';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    addresses,
    loading,
    error,
    fetchAddresses,
    createAddress,
    updateAddress,
    pingAddress,
    deleteAddress,
    initialType,
  };
};

export default useSavedAddresses;
